import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Cloud, CloudOff, LoaderCircle, LogOut, Pencil, RefreshCw } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';
import { cn } from '../lib/cn';
import { signIn, signOut, signUp, supabase, useSyncState } from './useSync';
import { ChangePassword, ForgotPassword } from './PasswordForms';
import { server, serverConfigured } from './config';
import { ConnectForm, ServerInfo } from './ServerSettings';

const field = 'w-full rounded-lg border border-border bg-bg px-2 py-1.5 text-sm';

/** Sign in / account creation, and sync status once signed in. */
export function AccountSettings() {
  if (!serverConfigured()) return <ConnectForm />;
  return (
    <>
      <ServerInfo />
      <ServerAccount />
    </>
  );
}

function ServerAccount() {
  const { t, i18n } = useTranslation();
  const { session, status, engine } = useSyncState();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [message, setMessage] = useState<{ tone: 'error' | 'info'; text: string } | null>(null);
  const fmt = new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short', dateStyle: 'short' });

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      setMessage({ tone: 'error', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  if (session) {
    return (
      <div className="flex flex-col gap-2 text-sm">
        <p>
          {t('sync.signedInAs')} <strong>{session.user.email}</strong>
        </p>
        <DisplayNameField session={session} />
        <SyncBadge />
        {status.lastSyncAt && (
          <p className="text-xs text-fg-muted">
            {t('sync.last', { time: fmt.format(status.lastSyncAt) })}
          </p>
        )}
        {status.error && status.state === 'error' && (
          <p className="text-xs text-danger">{status.error}</p>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            disabled={status.state === 'syncing'}
            onClick={() => void engine?.sync().catch(() => {})}
            className="flex items-center gap-1.5 rounded-lg border border-border px-2 py-1 hover:bg-bg-hover disabled:opacity-50"
          >
            <RefreshCw className="size-3.5" />
            {t('sync.now')}
          </button>
          <button
            type="button"
            onClick={() => void run(() => signOut())}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-fg-muted hover:bg-bg-hover"
          >
            <LogOut className="size-3.5" />
            {t('sync.signOut')}
          </button>
        </div>
        <ChangePassword />
      </div>
    );
  }

  if (forgot) return <ForgotPassword initialEmail={email} onBack={() => setForgot(false)} />;

  return (
    <form
      className="flex flex-col gap-2 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        void run(() => signIn(email.trim(), password));
      }}
    >
      <p className="text-xs text-fg-muted">{t('sync.intro')}</p>
      <label className="flex flex-col gap-1">
        {t('sync.email')}
        <input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={field}
        />
      </label>
      <label className="flex flex-col gap-1">
        {t('sync.password')}
        <input
          type="password"
          required
          minLength={8}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={field}
        />
      </label>
      {message && (
        <p
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={cn('text-xs', message.tone === 'error' ? 'text-danger' : 'text-fg-muted')}
        >
          {message.text}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="rounded-full bg-accent px-3 py-1.5 font-medium text-accent-fg disabled:opacity-50"
        >
          {t('sync.signIn')}
        </button>
        {server().allowSignup && (
          <button
            type="button"
            disabled={busy || !email || password.length < 8}
            onClick={() =>
              void run(async () => {
                const { needsConfirmation } = await signUp(email.trim(), password);
                setMessage({
                  tone: 'info',
                  text: needsConfirmation ? t('sync.confirmEmail') : t('sync.created'),
                });
              })
            }
            className="rounded-md px-3 py-1.5 hover:bg-bg-hover disabled:opacity-50"
          >
            {t('sync.signUp')}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => setForgot(true)}
        className="self-start text-xs text-fg-muted underline hover:text-fg"
      >
        {t('password.forgot')}
      </button>
      <p className="text-xs text-fg-muted">{t('sync.merge')}</p>
    </form>
  );
}

/** The display name shown to other members (workspaces, userbeheer); self-service. */
function DisplayNameField({ session }: { session: Session }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const name = (session.user.user_metadata?.display_name as string | undefined) ?? '';

  const save = async (value: string) => {
    setEditing(false);
    const next = value.trim();
    if (next === name) return;
    setBusy(true);
    try {
      await supabase().auth.updateUser({ data: { display_name: next || null } });
    } finally {
      setBusy(false);
    }
  };

  if (editing)
    return (
      <input
        autoFocus
        defaultValue={name}
        aria-label={t('management.nameLabel')}
        placeholder={session.user.email}
        onBlur={(e) => void save(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setEditing(false);
        }}
        className="w-full rounded-lg border border-border bg-bg px-2 py-1 text-sm"
      />
    );
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => setEditing(true)}
      className="flex items-center gap-1.5 self-start text-xs text-fg-muted hover:text-fg disabled:opacity-50"
    >
      {busy ? <Check className="size-3" /> : <Pencil className="size-3" />}
      {name || t('management.addName')}
    </button>
  );
}

/** Small status line: synced / syncing / offline / error / pending changes. */
export function SyncBadge({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const { session, status } = useSyncState();
  if (!session)
    return compact ? null : <span className="text-xs text-fg-muted">{t('sync.signedOut')}</span>;
  const icon =
    status.state === 'syncing' ? (
      <LoaderCircle className="size-3.5 animate-spin" />
    ) : status.state === 'offline' || status.state === 'error' ? (
      <CloudOff className="size-3.5" />
    ) : (
      <Cloud className="size-3.5" />
    );
  const label =
    status.state === 'syncing'
      ? t('sync.syncing')
      : status.state === 'offline'
        ? t('sync.offline', { count: status.pending })
        : status.state === 'error'
          ? t('sync.error')
          : status.pending > 0
            ? t('sync.pending', { count: status.pending })
            : t('sync.synced');
  return (
    <span
      role="status"
      aria-label={label}
      title={status.error ?? label}
      className={cn(
        'flex items-center gap-1.5 text-xs',
        status.state === 'error' ? 'text-danger' : 'text-fg-muted',
      )}
    >
      {icon}
      {!compact && label}
    </span>
  );
}
